import dataservice from "../../core/services/dataservice";
import _ from "lodash";
import { watch } from "vue";
import {
  computeBalance,
  computeItemByDate,
  computeLastUpdate,
  computeSummaryRange,
  selectRowsInRange,
  sortRowsByKey,
} from "../stock/stockMath";
import { StockEngineDisposedError, type StockEngine } from "../stock/stockSyncEngine";
import { getStockHandle, wipeAllStockCaches } from "../stock/useStockTransactions";
import type { StockRow } from "../stock/stockCheckpoint";

const MAX_CONCURRENT = 50;

/**
 * หน้าสต๊อกทำงานได้สองแบบ เลือกด้วยฟิลด์ `stock_checkpoint` (root ของ doc) ใน Firestore app_control/{channel}
 *
 *   legacy (flag ไม่เป็น true / อ่านไม่ได้) : ให้ server คำนวณผ่าน /inventories-tx/* — โค้ดเดิมไม่แก้
 *   local  (flag = true)                   : เก็บแถว ledger ทั้งร้านใน IndexedDB, sync ด้วย checkpoint
 *                                            ผ่าน /inventories-v2/sync แล้วคำนวณในเครื่องด้วย stockMath
 *
 * ชื่อ action / state เดิมคงไว้ทั้งหมด หน้าจอเดิมเรียกได้เหมือนเดิมทั้งสองโหมด
 */
export type StockMode = "legacy" | "local";
export type LocalStockStatus = "idle" | "loading" | "ready" | "error";

// local mode: เข้าหน้า / แท็บกลับมาโฟกัส sync ไม่ถี่กว่านี้ (ปุ่ม Reload ไม่นับ)
const SYNC_THROTTLE_MS = 30 * 1000;

// handle ที่ผูก listener แล้ว (หลัง reset ได้ handle ใหม่ จะผูกใหม่เอง)
const boundHandles = new WeakSet<object>();
// เวลาที่ sync ร้านนั้นสำเร็จล่าสุด (ใช้ throttle)
const lastSyncAt = new Map<string, number>();
// store ที่ฟัง flag แล้ว (ต่อ instance ของ pinia ไม่ใช่ทั้งโมดูล)
const watchedStores = new WeakSet<object>();

export const useInventoryTransactionStore = defineStore(
  "inventoryTransaction",
  {
    state: () => {
      return {
        balanceLoading: false,
        balance: {},
        lastUpdate: {},
        hash: "adew",

        startDate: "",
        endDate: "",
        reportValue: {},
        reportSummaryRange: {
          data: {},
          loading: false,
        },
        reportStockItemByDate: {
          name: "",
          stockId: "",
          data: [],
          loading: false,
        },

        // ---- local mode ----
        mode: "legacy" as StockMode,
        localStatus: "idle" as LocalStockStatus,
        errorMessage: "",
        // มีประวัติครบทั้งร้านในเครื่องแล้ว (ตัวเลขเชื่อได้)
        hasLocalCache: false,
        localRowCount: 0,
        // เคยโหลดยอดอย่างน้อยครั้งหนึ่ง (flag เปลี่ยนกลางทางจะได้รู้ว่าต้องโหลดใหม่ไหม)
        balanceRequested: false,
      };
    },
    getters: {
      getBalanceByStockId: (state) => (id) => {
        return state.balance[id];
      },

      getLastUpdateByStockId: (state) => (id) => {
        return state.lastUpdate[id];
      },

      /** local mode โหลดประวัติไม่สำเร็จและไม่มีแคชให้ใช้ — ตัวเลขบนจอเชื่อไม่ได้ ต้องแสดง error แทน */
      historyFailed: (state) =>
        state.mode === "local" && state.localStatus === "error" && !state.hasLocalCache,

      /** local mode ยังไม่มีประวัติครบ (กำลังโหลดครั้งแรก / โหลดไม่สำเร็จ) — legacy เป็น false เสมอ */
      balanceUnavailable: (state) => state.mode === "local" && !state.hasLocalCache,

      /** local mode sync รอบล่าสุดไม่สำเร็จ แต่ยังมีแคชให้ใช้ — ตัวเลขอาจไม่ล่าสุด */
      syncStale: (state) =>
        state.mode === "local" && state.localStatus === "error" && state.hasLocalCache,
    },

    actions: {
      init() {
        this._startWatchers();
        // legacy โหลดยอดตอนบูตเหมือนเดิม
        // local ไม่โหลดตอนบูต (เครื่อง POS ที่ไม่เคยเปิดหน้าสต๊อกจะได้ไม่ต้องดึงประวัติทั้งร้าน) รอหน้าสต๊อกเรียกเอง
        this._resolveMode().then((mode) => {
          if (mode === "legacy") return this.fetchBalance();
        }).catch((error) => console.error("[stock] init failed", error));
      },

      //
      async fetchBalance() {
        this.balanceRequested = true;
        return this._run(
          () => this._fetchBalanceLocal(),
          async () => {
            this.balanceLoading = true;
            let res = await dataservice.invetoryTransaction.getBalance();
            this.balance = res.data.balance;
            this.lastUpdate = res.data.lastUpdate;
            this.balanceLoading = false;
          }
        );
      },

      /** ปุ่ม Reload — local: ล้างแคชร้านนี้แล้วโหลดใหม่ทั้งหมด · legacy: เหมือน fetchBalance เดิม */
      async reloadAll() {
        this.balanceRequested = true;
        return this._run(
          async () => {
            this.balanceLoading = true;
            try {
              await this._syncLocalOrNull({ reload: true });
            } finally {
              this.balanceLoading = false;
            }
          },
          () => this.fetchBalance()
        );
      },

      async fetchReportSummaryRange(startDate, endDate) {
        this.reportSummaryRange = {
          data: {},
          loading: true,
        };

        return this._run(
          async () => {
            // โหลดไม่สำเร็จ = ตารางว่าง + หน้าจอเห็น historyFailed (ไม่ throw ให้หน้าค้างสถานะโหลด)
            const engine = await this._syncLocalOrNull();
            this.reportSummaryRange.data = engine
              ? computeSummaryRange(engine.rows(), startDate, endDate)
              : {};
            this.reportSummaryRange.loading = false;
          },
          async () => {
            let payload = {
              startDate: startDate,
              endDate: endDate,
            };
            let res = await dataservice.invetoryTransaction.reportSummaryRange(
              payload
            );

            this.reportSummaryRange.data = res.data.data;
            this.reportSummaryRange.loading = false;
          }
        );
      },

      async fethcReportStockItemByDate(stock) {
        let data = {
          stockId: stock.stockId,
        };

        this.reportStockItemByDate = {
          name: stock.name,
          stockId: stock.stockId,
          data: [],
          loading: true,
        };

        return this._run(
          async () => {
            const engine = await this._syncLocalOrNull();
            // ผู้ใช้กดตัวอื่นไปแล้วระหว่างรอ — ไม่เขียนทับของตัวใหม่
            if (this.reportStockItemByDate.stockId !== stock.stockId) return;
            this.reportStockItemByDate.data = engine
              ? computeItemByDate(engine.rows(), stock.stockId)
              : [];
            this.reportStockItemByDate.loading = false;
          },
          async () => {
            let res = await dataservice.invetoryTransaction.reportStockItemByDate(
              data
            );
            this.reportStockItemByDate.data = res.data.data;
            this.reportStockItemByDate.loading = false;
          }
        );
      },

      // คืน array ดิบจาก backend ให้ผู้เรียกไป map เอง ไม่เก็บลง state
      // เพราะหน้า history ถือรายการของตัวเองแยกตามเดือนที่เลือก
      async fetchBetweenDates(startDate, endDate) {
        return this._run(
          async () => {
            const engine = await this._syncLocal();
            // เรียงตาม key2 เหมือนที่ DynamoDB Query คืน
            return sortRowsByKey(selectRowsInRange(engine.rows(), startDate, endDate));
          },
          async () => {
            let res = await dataservice.invetoryTransaction.fetchBetweenDates(
              startDate,
              endDate
            );
            return res.data || [];
          }
        );
      },

      // ประวัติรายบรรทัดของสต็อก 1 ตัวในวันเดียว คืน array ดิบให้ modal ไป render เอง
      // อยู่คนละตาราง (XTABLE_STOCK_LOGS) ไม่ได้อยู่ใน ledger — ยิง server ทั้งสองโหมด
      async fetchStockLogsByDate(stockId, date) {
        let res = await dataservice.invetoryTransaction.fetchStockLogsByDate(
          stockId,
          date
        );
        return (res.data && res.data.logs) || [];
      },

      async insert(data) {
        return this._run(
          () => this._insertLocal(data),
          async () => {
            await dataservice.invetoryTransaction.post(data);
          }
        );
      },

      // -------------------------------------------------------------------
      // ภายใน
      // -------------------------------------------------------------------

      /** รอค่า flag (snapshot แรกไม่เกิน ~3 วิ) แล้วตั้งโหมด */
      async _resolveMode(): Promise<StockMode> {
        const enabled = await useAppControlStore().waitForStockCheckpointFlag();
        const mode: StockMode = enabled ? "local" : "legacy";
        this._setMode(mode);
        return mode;
      },

      _setMode(mode: StockMode) {
        if (this.mode === mode) return;
        this.mode = mode;
        this.localStatus = "idle";
        this.errorMessage = "";
        this.hasLocalCache = false;
        this.localRowCount = 0;
      },

      /**
       * เลือกทางตามโหมด
       * engine ถูกปิดกลางทาง (flag ถูกปิด -> ลบแคช) -> ดูโหมดใหม่แล้วทำอีกรอบเดียว
       */
      async _run<T>(localFn: () => Promise<T>, legacyFn: () => Promise<T>): Promise<T> {
        for (let attempt = 0; ; attempt++) {
          const mode = await this._resolveMode();
          if (mode === "legacy") return legacyFn();
          try {
            return await localFn();
          } catch (error) {
            if (error instanceof StockEngineDisposedError && attempt === 0) continue;
            throw error;
          }
        }
      },

      _storeId(): string {
        return useShopStore().storeId || "";
      },

      /** ผูก listener ครั้งเดียวต่อ handle: แถวในเครื่องเปลี่ยน -> คำนวณยอดใหม่ */
      _bind(handle: { engine: StockEngine; listeners: Set<(rows: StockRow[]) => void> }, storeId: string) {
        if (boundHandles.has(handle)) return;
        boundHandles.add(handle);
        handle.listeners.add((rows) => {
          // flag ถูกปิด / ร้านเปลี่ยนไปแล้ว -> ห้ามเอายอดของ engine นี้มาแสดง
          if (this.mode !== "local" || this._storeId() !== storeId) return;
          const complete = handle.engine.hasCompleteCache();
          this.hasLocalCache = complete;
          this.localRowCount = rows.length;
          this.balance = computeBalance(rows);
          this.lastUpdate = computeLastUpdate(rows);
          if (complete) this.balanceLoading = false;
        });
      },

      /**
       * sync แถวของร้านปัจจุบัน แล้วคืน engine ให้คำนวณต่อ
       * - ยังไม่ครบ 30 วิจาก sync ล่าสุดและมีแคชครบ -> ใช้ของในเครื่องเลย ไม่ยิง
       * - sync พังแต่มีแคชครบ -> ใช้แคช (status = error ให้หน้าจอเตือนได้)
       * - sync พังและไม่มีแคช -> throw
       */
      async _syncLocal(opts: { reload?: boolean } = {}): Promise<StockEngine> {
        const storeId = this._storeId();
        let handle: Awaited<ReturnType<typeof getStockHandle>>;
        try {
          handle = await getStockHandle(storeId);
        } catch (error: any) {
          // ยังไม่รู้ว่าร้านไหน / เปิด db ไม่ได้ — ต้องเห็นเป็น error ไม่ใช่ยอด 0 เงียบ ๆ
          this.localStatus = "error";
          this.errorMessage = String(error?.message || error);
          this.hasLocalCache = false;
          throw error;
        }
        const { engine } = handle;
        this._bind(handle, storeId);

        const fresh = Date.now() - (lastSyncAt.get(storeId) || 0) < SYNC_THROTTLE_MS;
        if (!opts.reload && fresh && engine.hasCompleteCache()) return engine;

        this.localStatus = "loading";
        try {
          await (opts.reload ? engine.reload() : engine.sync());
          lastSyncAt.set(storeId, Date.now());
          if (this._storeId() === storeId) {
            this.localStatus = "ready";
            this.errorMessage = "";
          }
        } catch (error: any) {
          if (error instanceof StockEngineDisposedError) throw error;
          console.error("[stock] sync failed", error);
          if (this._storeId() === storeId) {
            this.localStatus = "error";
            this.errorMessage = String(error?.message || error);
            this.hasLocalCache = engine.hasCompleteCache();
          }
          if (!engine.hasCompleteCache()) throw error;
        }
        return engine;
      },

      /** เหมือน _syncLocal แต่โหลดไม่สำเร็จคืน null (สถานะ error อยู่ใน state ให้หน้าจอแสดงแทน) */
      async _syncLocalOrNull(opts: { reload?: boolean } = {}): Promise<StockEngine | null> {
        try {
          return await this._syncLocal(opts);
        } catch (error) {
          if (error instanceof StockEngineDisposedError) throw error;
          return null;
        }
      },

      async _fetchBalanceLocal() {
        const storeId = this._storeId();
        const handle = await getStockHandle(storeId).catch(() => null);
        // ยังไม่มีอะไรจะวาด -> ขึ้นหน้าโหลด  มีแคชแล้ว -> sync เงียบ ๆ ข้างหลัง
        if (!handle?.engine.hasCompleteCache()) this.balanceLoading = true;
        try {
          const engine = await this._syncLocalOrNull();
          if (!engine || this._storeId() !== storeId) return;
          const rows = engine.rows();
          this.hasLocalCache = engine.hasCompleteCache();
          this.localRowCount = rows.length;
          this.balance = computeBalance(rows);
          this.lastUpdate = computeLastUpdate(rows);
        } finally {
          this.balanceLoading = false;
        }
      },

      async _insertLocal(data) {
        const storeId = this._storeId();
        // ยังไม่รู้ storeId (ไม่ควรเกิด) -> ให้ dataservice เติม storeId/createdAt เองแบบเดิม ไม่ใส่ลงเครื่อง
        if (!storeId) {
          await dataservice.invetoryTransaction.post(data);
          return;
        }
        // ตั้ง storeId + createdAt เอง (dataservice.post ตั้ง createdAt ให้เฉพาะตอนไม่มี storeId)
        // key2 ฝั่ง server = businessDate + ':' + createdAt -> แถวในเครื่องได้ key เดียวกันเป๊ะ
        const row: StockRow = {
          ...data,
          storeId,
          createdAt: data.createdAt || new Date().toISOString(),
        };
        await dataservice.invetoryTransaction.post(row);

        // POST สำเร็จแล้ว ห้าม throw ต่อจากนี้ (ผู้ใช้จะกดบันทึกซ้ำ = ธุรกรรมซ้ำ)
        // ใส่แถวลงเครื่องทันที แถวเดียวกันจะกลับมาจาก change log อีกรอบเมื่อ stream ตามทัน
        try {
          lastSyncAt.delete(storeId);
          const handle = await getStockHandle(storeId);
          this._bind(handle, storeId);
          if (handle.engine.hasCompleteCache()) await handle.engine.applyLocal(row);
        } catch (error) {
          console.warn("[stock] ใส่แถวที่เพิ่งบันทึกลงเครื่องไม่สำเร็จ รอ sync รอบหน้า", error);
        }
      },

      /** เริ่มครั้งเดียว: ฟัง flag + sync ตอนแท็บกลับมาโฟกัส */
      _startWatchers() {
        if (watchedStores.has(this) || typeof window === "undefined") return;
        watchedStores.add(this);

        const appControl = useAppControlStore();
        watch(
          () => [appControl.snapshotReceived, appControl.stockCheckpointEnabled] as const,
          ([received, enabled]) => {
            if (!received) return;
            // flag ไม่เป็น true = checkpoint ในเครื่องเชื่อไม่ได้แล้ว ลบ database ของทุกร้าน (ไม่มีแคช = ไม่ทำอะไร)
            // (logout / สลับร้าน ไม่ลบ — มีแค่กรณีนี้ที่ลบ)
            if (!enabled) {
              lastSyncAt.clear();
              wipeAllStockCaches();
            }
            const next: StockMode = enabled ? "local" : "legacy";
            if (this.mode === next) return;
            this._setMode(next);
            // หน้าที่เปิดอยู่ได้ยอดจากโหมดใหม่หนึ่งครั้ง
            if (this.balanceRequested) {
              this.fetchBalance().catch((error) => console.error("[stock] refetch after flag change failed", error));
            }
          },
          { immediate: true }
        );

        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState !== "visible") return;
          if (this.mode !== "local" || !this.balanceRequested) return;
          this._syncLocal().catch(() => {});
        });
      },
    },
  }
);
